import { Badge } from "@/components/ui/badge";
import { cn } from '@flowstate/utils';

interface MechaTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assigned_to: string | null;
}

interface MechaTasksData {
  tasks: MechaTask[];
  count: number;
  query_description: string;
  filters: Record<string, unknown>;
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-red-500/15 text-red-400 border-red-500/30",
  urgent: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  high: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  medium: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  low: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const STATUS_COLORS: Record<string, string> = {
  todo: "bg-slate-500/15 text-slate-400",
  pending: "bg-yellow-500/15 text-yellow-400",
  in_progress: "bg-blue-500/15 text-blue-400",
  completed: "bg-green-500/15 text-green-400",
  cancelled: "bg-red-500/15 text-red-300",
  blocked: "bg-red-500/15 text-red-400",
  on_hold: "bg-purple-500/15 text-purple-400",
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays < -1) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays <= 7) return `In ${diffDays}d`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

interface MechaTasksBlockProps {
  data: MechaTasksData;
}

export function MechaTasksBlock({ data }: MechaTasksBlockProps) {
  const { tasks, count, query_description } = data;

  return (
    <div className="rounded-lg border border-border/50 bg-card/50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 bg-muted/30">
        <h3 className="text-sm font-medium text-foreground">{query_description}</h3>
        <Badge variant="outline" className="text-xs">
          {count} {count === 1 ? "task" : "tasks"}
        </Badge>
      </div>

      {tasks.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          No tasks found matching this query.
        </div>
      ) : (
        <div className="divide-y divide-border/20">
          {tasks.slice(0, 15).map((task) => (
            <div
              key={task.id}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/20 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{task.title}</p>
              </div>
              <Badge
                variant="outline"
                className={cn("text-[10px] shrink-0", STATUS_COLORS[task.status] || "")}
              >
                {task.status.replace(/_/g, " ")}
              </Badge>
              <Badge
                variant="outline"
                className={cn("text-[10px] shrink-0", PRIORITY_COLORS[task.priority] || "")}
              >
                {task.priority}
              </Badge>
              <span className="text-xs text-muted-foreground shrink-0 w-20 text-right">
                {formatDate(task.due_date)}
              </span>
            </div>
          ))}
          {count > 15 && (
            <div className="px-4 py-2 text-xs text-muted-foreground text-center">
              ...and {count - 15} more tasks
            </div>
          )}
        </div>
      )}
    </div>
  );
}
